/**
 * WorkflowStepper — Horizontal MUI Stepper showing the 5-stage
 * clinical radiology pipeline. State-driven, no manual navigation.
 *
 * Steps: Record → Correct → CPT Exam → Generate → Review
 */
import React, { useEffect, useRef } from 'react';
import { Stepper, Step, StepLabel, StepIconProps, Box, Tooltip } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import DescriptionIcon from '@mui/icons-material/Description';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useAppSelector } from '../hooks/reduxHooks';
import {
  WORKFLOW_STEPS,
  selectStepStatuses,
  selectWorkflowStage,
  type StepStatus,
  type WorkflowStage,
} from '../store/workflowSelectors';
import { typography } from '~/theme';

// ── Icon mapping per step ───────────────────────────────────────────

const STEP_ICONS: Record<WorkflowStage, React.ReactElement> = {
  record: <MicIcon />,
  correct: <AutoFixHighIcon />,
  cpt_detect: <LocalHospitalIcon />,
  generate: <DescriptionIcon />,
  review: <FactCheckIcon />,
};

// ── Colors per status ───────────────────────────────────────────────

const STATUS_COLORS: Record<StepStatus, string> = {
  completed: '#059669',
  active: '#7bc9a3',
  waiting: '#d97706',
  error: '#dc2626',
  inactive: '#94a3b8',
};

// ── Tooltip text per status ─────────────────────────────────────────

function getTooltip(label: string, status: StepStatus): string {
  switch (status) {
    case 'completed': return `${label} — Complete`;
    case 'active': return `${label} — In Progress`;
    case 'waiting': return `${label} — Processing...`;
    case 'error': return `${label} — Error`;
    case 'inactive': return `${label} — Pending`;
  }
}

// ── Custom Step Icon ────────────────────────────────────────────────

interface WorkflowStepIconProps extends StepIconProps {
  stepKey: WorkflowStage;
  stepStatus: StepStatus;
}

const WorkflowStepIcon: React.FC<WorkflowStepIconProps> = ({ stepKey, stepStatus }) => {
  const color = STATUS_COLORS[stepStatus];

  const icon = (() => {
    if (stepStatus === 'completed') return <CheckCircleIcon sx={{ fontSize: 22 }} />;
    if (stepStatus === 'error') return <ErrorIcon sx={{ fontSize: 22 }} />;
    if (stepStatus === 'waiting') return <HourglassEmptyIcon sx={{ fontSize: 22 }} />;
    return React.cloneElement(STEP_ICONS[stepKey], { sx: { fontSize: 22 } });
  })();

  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: stepStatus === 'active' ? `${color}14` : 'transparent',
        border: `2px solid ${color}`,
        color,
        transition: 'all 0.3s ease',
        ...(stepStatus === 'active' && {
          boxShadow: `0 0 0 3px ${color}22`,
        }),
        ...(stepStatus === 'waiting' && {
          animation: 'workflowPulse 1.8s ease-in-out infinite',
          '@keyframes workflowPulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.5 },
          },
        }),
      }}
    >
      {icon}
    </Box>
  );
};

// ── Main Component ──────────────────────────────────────────────────

const WorkflowStepper: React.FC = () => {
  const stepStatuses = useAppSelector(selectStepStatuses);
  const activeStage = useAppSelector(selectWorkflowStage);
  const prevStageRef = useRef<WorkflowStage>(activeStage);

  // Log stage transitions
  useEffect(() => {
    if (prevStageRef.current !== activeStage) {
      console.log(
        'WORKFLOW_STAGE_CHANGED | from=%s to=%s',
        prevStageRef.current,
        activeStage,
      );
      prevStageRef.current = activeStage;
    }
  }, [activeStage]);

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stepper
        alternativeLabel
        activeStep={-1}
        sx={{
          '& .MuiStepConnector-line': {
            borderTopWidth: 2,
            transition: 'border-color 0.3s ease',
          },
        }}
      >
        {WORKFLOW_STEPS.map((step, idx) => {
          const status = stepStatuses[idx];
          const connectorColor =
            status === 'completed' ? STATUS_COLORS.completed : STATUS_COLORS.inactive;

          return (
            <Step
              key={step.key}
              completed={status === 'completed'}
              sx={{
                '& .MuiStepConnector-line': {
                  borderColor: connectorColor,
                },
              }}
            >
              <Tooltip title={getTooltip(step.label, status)} arrow placement="top">
                <StepLabel
                  StepIconComponent={(props) => (
                    <WorkflowStepIcon {...props} stepKey={step.key} stepStatus={status} />
                  )}
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontSize: typography.caption.fontSize,
                      fontWeight: status === 'active' ? 700 : 500,
                      color: STATUS_COLORS[status],
                      mt: 0.5,
                      transition: 'all 0.3s ease',
                    },
                  }}
                >
                  {step.label}
                </StepLabel>
              </Tooltip>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

export default WorkflowStepper;
